// Frosted hero cards (Tile, MiniCard), dark lower blocks and glass cards.
import i18n from '@/i18n';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '@/lib/format';
import { ArrowChip } from './Buttons';
import { CoinIcon } from './CoinIcon';
import { IconCircle } from './Icon';

type TileProps = {
  icon: string;
  /** coin symbol instead of a line icon */
  coin?: string;
  label: ReactNode;
  big: ReactNode;
  unit?: ReactNode;
  viz?: ReactNode;
  extra?: ReactNode;
  hl?: boolean;
  className?: string;
  to?: string;
  onClick?: () => void;
  data?: Record<string, string | number>;
};

/** Hero tile: icon + arrow chip, label, big number with unit, tiny visual. */
export function Tile({ icon, coin, label, big, unit, viz, extra, hl, className, to, onClick, data }: TileProps) {
  const body = (
    <>
      <div className="tile-top">
        {coin ? <span className="tile-coin"><CoinIcon symbol={coin} /></span> : <IconCircle name={icon} />}
        {(to || onClick) && <ArrowChip label={to ? i18n.t('common:action.open') : i18n.t('common:action.show')} />}
      </div>
      <span className="tile-l">{label}</span>
      <div className="tile-b"><b>{big}<small>{unit}</small></b>{viz}</div>
      {extra}
    </>
  );
  const cls = cx('tile', hl && 'hl', !to && !onClick && 'static', className);
  const dataAttrs = Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v]));
  if (!to && !onClick) return <div className={cls} {...dataAttrs}>{body}</div>;
  return to
    ? <Link className={cls} to={to} {...dataAttrs}>{body}</Link>
    : <button className={cls} type="button" onClick={onClick} {...dataAttrs}>{body}</button>;
}

export type MiniCardProps = {
  icon: string;
  label: ReactNode;
  pre?: string;
  value: ReactNode;
  cents?: ReactNode;
  /** a route, or an action; with neither the card is a plain stat (no arrow) */
  to?: string;
  onClick?: () => void;
  badge?: boolean;
};

/** Small frosted card under the hero metric. */
export function MiniCard({ icon, label, pre = '', value, cents = '', to, onClick, badge }: MiniCardProps) {
  const inner = (
    <>
      <div className="tile-top"><IconCircle name={icon} badge={badge} />{(to || onClick) && <ArrowChip />}</div>
      <div className="mini-b"><span>{label}</span><b><small className="pre">{pre}</small>{value}<small>{cents}</small></b></div>
    </>
  );
  if (to) return <Link className="mini" to={to}>{inner}</Link>;
  if (onClick) return <button type="button" className="mini" onClick={onClick}>{inner}</button>;
  return <div className="mini static">{inner}</div>;
}

/** Dashed "add" slot styled as a pressed-in well with a raised plus. */
export function AddTile({ label, to, onClick }: { label: string; to?: string; onClick?: () => void }) {
  const inner = <><span className="plus"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg></span><span>{label}</span></>;
  return to
    ? <Link className="tile tile-add" to={to}>{inner}</Link>
    : <button className="tile tile-add" type="button" onClick={onClick}>{inner}</button>;
}

type BlockProps = {
  title?: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  span?: number;
  className?: string;
  id?: string;
  children?: ReactNode;
};

/** Card of the lower section (`.blk`) on the 12-column grid. */
export function ContentBlock({ title, sub, right, span = 12, className, id, children }: BlockProps) {
  return (
    <section className={cx('blk', className)} style={{ '--span': span } as CSSProperties} id={id}>
      {title && (
        <header className="blk-h">
          <div><h3>{title}</h3>{sub && <span className="blk-s">{sub}</span>}</div>
          <div className="blk-r">{right}</div>
        </header>
      )}
      {children}
    </section>
  );
}

/** Glass card over a blurred scene image (overview "Главное для вас"). */
export function GlassCard({ image, icon, title, stats, to }: { image: string; icon: string; title: ReactNode; stats: Array<[string, ReactNode]>; to: string }) {
  return (
    // the article's own picture behind the glass, or the plain card when there is none
    <Link className={cx('gcard', !image && 'noimg')} to={to} style={image ? { '--img': `url('${image.replace(/'/g, '%27')}')` } as CSSProperties : undefined}>
      <div className="gcard-top"><IconCircle name={icon} /><p>{title}</p></div>
      <div className="gcard-stats">
        {stats.map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}
        <ArrowChip />
      </div>
    </Link>
  );
}
